import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function OrganizationCreatePage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [type, setType] = useState("school");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.createOrganization({
        name,
        type: type as "platform" | "school" | "club",
      });
      navigate("/organizations");
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to create organization");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6" data-page="organization-create" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Add Organization</h1>
        <p className="text-muted-foreground">Create a new organization</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Organization Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="organization-create">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Organization Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder="e.g. Camp Discovery Schools"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="type">Type</Label>
              <select
                id="type"
                title="Organization Type"
                data-field="type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="platform">Platform</option>
                <option value="school">School</option>
                <option value="club">Club</option>
              </select>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create Organization"}
              </Button>
              <Link to="/organizations" className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
